import type React from 'react';
import { useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useShallow } from 'zustand/react/shallow';
import type { GameV1 } from '@/api/games';
import { Button } from '@/components/buttons';
import type { ColorMode } from '@/components/GameItem/styled';
import type { ScGameItemPlugProps } from '@/components/GamesGrid/styled';
import { NativeSlider } from '@/components/NativeSlider';
import { SliderArrow } from '@/components/NativeSlider/helpers';
import { SectionTitle } from '@/components/SectionTitle';
import GamesSkeleton from '@/components/skeletons/GamesSkeleton';
import { createItems } from '@/helpers/common';
import type { GameItemMode } from '@/helpers/enums';
import { createGamesListName, getGamesPlugs } from '@/helpers/game';
import { getSliderParams } from '@/helpers/getSliderParams';
import { useNavigation } from '@/hooks/navigation';
import { useResponsive } from '@/modules/client';
import { useGetCommonStore } from '@/store';
import { useCMSGameCategories } from '../../hooks';
import { type LobbyGamesSlideProps, Slide } from './components/Slide/Slide';

interface PerSliderItem {
  cols: number;
  allGames: number;
  rows: number;
  slidesPerView?: number;
}

export type SliderParams = {
  desktopL: PerSliderItem;
  desktop: PerSliderItem;
  tablet: PerSliderItem;
  mobile: PerSliderItem;
  mobileS: PerSliderItem;
};

interface LobbyGamesSliderProps extends Pick<ScGameItemPlugProps, 'plugBlack'> {
  sliderParams: SliderParams;
  categoryId: string;
  subCategoryId?: string;
  title: string | React.ReactElement;
  description?: string;
  titleIcon?: string | React.ReactElement;
  gameItemMode?: GameItemMode;
  lazyLoadingOf?: number;
  titleMultiRow?: boolean;
  darkArrows?: boolean;
  noAllButton?: boolean;
  noArrows?: boolean;
  blackShowAllCard?: boolean;
  gameColorMode?: ColorMode;
  gamesList?: GameV1[];
  plugsOnEmpty?: boolean;
  onGamesLoad?: (games: GameV1[]) => void;
  gamesLength?: number;
  subName?: string;
  seeAllLink?: string;
  hideGamesCount?: boolean;
}

const LobbyGamesSlider = ({
  sliderParams,
  categoryId,
  titleIcon,
  title,
  description,
  subCategoryId,
  titleMultiRow,
  blackShowAllCard,
  gameColorMode,
  gamesList,
  onGamesLoad,
  darkArrows,
  plugsOnEmpty,
  gamesLength,
  subName,
  seeAllLink: seeAllLinkProp,
  hideGamesCount,
  ...props
}: LobbyGamesSliderProps) => {
  const { t } = useTranslation('common');
  const { currentMediaType } = useResponsive();
  const { generateLink } = useNavigation();

  const { categories: gameCategoriesList } = useCMSGameCategories();
  const useCommonStore = useGetCommonStore();

  const {
    isAuthenticated,
    gamesByCategories,
    pendingGamesByCategories,
    getGamesByCategory,
  } = useCommonStore(
    useShallow((state) => ({
      isAuthenticated: state.isAuthenticated,
      gamesByCategories: state.gamesByCategories,
      pendingGamesByCategories: state.pendingGamesByCategories,
      getGamesByCategory: state.getGamesByCategory,
    }))
  );

  const {
    cols,
    rows,
    allGames,
    slidesPerView = 1,
  } = useMemo(
    () => getSliderParams(sliderParams, currentMediaType),
    [currentMediaType, sliderParams]
  );

  const gamesParametr = useMemo(() => {
    if (!gameCategoriesList.length) return '';
    if (subCategoryId) return subCategoryId;

    const gameCategory = gameCategoriesList.find(
      ({ defaultCategoryId }) => defaultCategoryId === categoryId
    );
    if (!gameCategory) return '';

    return gameCategory.id;
  }, [isAuthenticated, gameCategoriesList, subCategoryId, categoryId]);

  const gamesCategoryKey = useMemo(() => {
    if (!subName) return gamesParametr;
    return createGamesListName(gamesParametr, subName);
  }, [gamesParametr, subName]);

  useEffect(() => {
    if (
      !(gameCategoriesList && gamesParametr) ||
      gamesByCategories[gamesCategoryKey] ||
      gamesList
    )
      return;

    getGamesByCategory(gamesParametr, {
      pageSize: allGames,
      staticAmount: true,
      subName,
    });
  }, [
    allGames,
    gamesList,
    gamesParametr,
    getGamesByCategory,
    gameCategoriesList,
    gamesByCategories,
    subName,
    gamesCategoryKey,
  ]);

  const { games = [], pagination } = useMemo(() => {
    if (gamesList || !gamesByCategories[gamesCategoryKey]) {
      return {
        games: gamesList,
        pagination: {
          totalCount: gamesList?.length || 0,
        },
      };
    }
    return gamesByCategories[gamesCategoryKey];
  }, [gamesByCategories, gamesCategoryKey, gamesList, subName]);

  useEffect(() => {
    if (games?.length && onGamesLoad) {
      onGamesLoad(games);
    }
  }, [games, onGamesLoad]);

  const seeAllLink = useMemo(() => {
    if (seeAllLinkProp) return seeAllLinkProp;

    const link = subCategoryId
      ? `/games/${categoryId}/sub-category/${subCategoryId}#gamesSection`
      : `/games/${categoryId}#gamesSection`;

    return generateLink(link);
  }, [
    categoryId,
    subCategoryId,
    gameCategoriesList,
    generateLink,
    seeAllLinkProp,
  ]);

  const slides: LobbyGamesSlideProps[] = useMemo(() => {
    const sliderGames =
      pagination.totalCount > allGames ? games.slice(0, allGames) : games;
    const chunkSize = rows * cols;

    const plugs = sliderGames.length
      ? sliderGames.length <= cols
        ? getGamesPlugs(sliderGames, cols, 1)
        : []
      : createItems({}, cols);

    if (!sliderGames.length)
      return [{ plugs, rows, cols, gameColorMode, ...props }];

    return Array.from(
      { length: Math.ceil(sliderGames.length / chunkSize) },
      (_, i) => {
        const slideGames = sliderGames.slice(
          i * chunkSize,
          i * chunkSize + chunkSize
        );

        const commonSlideProps = {
          games: slideGames,
          rows,
          cols,
          plugs: createItems({}, cols * rows - slideGames.length),
          blackShowAllCard,
          gameColorMode,
          ...props,
        };

        return pagination.totalCount > allGames - 1 &&
          sliderGames.length > chunkSize &&
          i * chunkSize === sliderGames.length - chunkSize
          ? {
              link: seeAllLink,
              ...commonSlideProps,
            }
          : commonSlideProps;
      }
    );
  }, [
    games,
    allGames,
    rows,
    cols,
    props,
    seeAllLink,
    blackShowAllCard,
    gameColorMode,
    pagination,
  ]);

  const loadingGames = useMemo(
    () => pendingGamesByCategories[gamesParametr],
    [pendingGamesByCategories, gamesParametr]
  );

  // Generate test ID based on category
  const sliderTestId = useMemo(() => {
    const categoryMap: Record<string, string> = {
      recent: 'recent-games-slider',
      wagering: 'wagering-games-slider',
      hits: 'hits-games-slider',
      exclusive: 'exclusive-games-slider',
      'top-vip': 'top-vip-games-slider',
      top: 'top-games-slider',
      new: 'new-games-slider',
      casual: 'casual-games-slider',
      'bonus-buy': 'bonus-buy-slider',
      'hot-and-cold': 'hot-and-cold-slider',
      'jackpot-category': 'jackpot-category-slider',
      live: 'live-casino-slider',
    };
    return (
      categoryMap[categoryId.toLowerCase()] ||
      `${categoryId.toLowerCase()}-slider`
    );
  }, [categoryId]);

  if (!(games.length || plugsOnEmpty || loadingGames))
    return <GamesSkeleton cols={cols} rows={rows} withTitle />;

  return loadingGames ? (
    <GamesSkeleton cols={cols} rows={rows} withTitle />
  ) : (
    <NativeSlider<LobbyGamesSlideProps>
      arrows={{
        type: SliderArrow.OUTSIDE,
        dark: darkArrows,
        componentLeft: (
          <SectionTitle multiRow={titleMultiRow}>
            <span className="title">
              {title}
              {titleIcon && <span className="after-title">{titleIcon}</span>}
            </span>
            {description && <span className="description">{description}</span>}
          </SectionTitle>
        ),
        componentRight: (
          <Button
            autoPaddingHorizontal
            className="all-btn"
            data-testid="show-all-button"
            id="gameCategoryButton"
            paddingX={8}
            to={seeAllLink}
            whiteTransparent
            xSmall
          >
            {hideGamesCount
              ? t('gameCategories.all')
              : `${t('gameCategories.all')} ${gamesLength || pagination.totalCount || 0}`}
          </Button>
        ),
      }}
      dataTestId={sliderTestId}
      slide={Slide}
      slides={slides}
      slidesPerView={slidesPerView}
    />
  );
};

export default LobbyGamesSlider;
